import { Div, Image } from '@kizi/core-components';
import _ from 'lodash';
import React from 'react';
import { PRODUCT_TYPE } from '../../../../src/funnels/index';
import { ComparisonSubmitButton } from '../../../components/comparison/ComparisonSubmitButton';
import {
  getCharacteristics,
  getDetailsMap,
  getEnergyLabelItemsList,
} from '../../../components/Product/ExpandedPart/functions';
import { ListComp } from '../../../components/Product/ExpandedPart/List';
import {
  getAllRates,
  Rates,
} from '../../../components/Product/ExpandedPart/Rates';
import { ProductUsps } from '../../../components/ProductUsps';
import { ProsCons } from '../../../components/ProsCons';
import { RatingReviews } from '../../../components/RatingReviews';
import { Sustainability } from '../../../components/Sustainability';
import { formatPrice } from '../../../utils/global';
import { Price } from '../../../utils/product';
import { getProductProps } from '../../product';

export const getProductComparisonOverview = (data) => {
  const _data = { ...data, type: 'energy' };

  const {
    bigProductFeature,
    image,
    name,
    sustainabilityScore,
    usps,
    review,
    total: totalMonth,
  } = getProductProps(_data, 'month');
  const { total: totalYear } = getProductProps(_data, 'year');

  const sustainabilityValue = sustainabilityScore
    ? String(Number(sustainabilityScore).toFixed(1))
    : '';

  return [
    {
      content: (
        <Div display="flex" flexDirection="column" alignItems="center">
          <Div display="flex" variant="productLogoWrapper" mb={[2, null, 5]}>
            {image ? (
              <Image
                alt={`logo-${name}`}
                height="100%"
                src={image}
                width="100%"
              />
            ) : (
              <Div height={40} />
            )}
          </Div>
          {review && <RatingReviews data={review} />}
        </Div>
      ),
      isVisible: true,
      toggleable: false,
    },
    {
      content: usps && (
        <Div flexDirection="column">
          <ProductUsps data={usps} />
        </Div>
      ),
      isVisible: true,
      toggleable: false,
    },
    {
      content: <Div>{formatPrice(bigProductFeature?.value)}</Div>,
      isVisible: true,
      toggleable: false,
    },
    {
      content: sustainabilityValue && (
        <Sustainability
          variant="sustainabilityRegular"
          score={sustainabilityValue}
        />
      ),
      isVisible: true,
      toggleable: false,
    },
    {
      content: totalYear && <Div>{formatPrice(totalYear)}</Div>,
      isVisible: true,
      toggleable: false,
    },
    {
      content: totalMonth && <Div>{formatPrice(totalMonth)}</Div>,
      isVisible: true,
      toggleable: false,
    },
    {
      content: (
        <Div display="flex" justifyContent="center">
          <ComparisonSubmitButton
            data={_data}
            fieldName="overview.selectedProduct"
            nextIndex={4}
            isProductEnabled={data.availability.acceptance.accepted}
          />
        </Div>
      ),
      isVisible: true,
      toggleable: false,
    },
  ];
};

export const handleProsNCons = (obj, keys) => {
  const allowedFilteredKeys = _.pick(obj, keys);
  const result = Object.fromEntries(
    Object.entries(allowedFilteredKeys).filter((value) => value[1].trim()),
  );
  return Object.keys(result).map((key) => [result[key]]);
};

export const getProductComparisonSupplier = (data) => {
  const _data = { ...data, type: 'energy' };
  const { attributes } = getProductProps(_data, 'year');
  const {
    prosAndCons,
    reviews,
    postcoderoosregeling,
    annual_account,
    edit_amount,
    meter_readings,
    payment_method,
    usage,
  } = attributes;
  const { expert_quote = '' } = reviews;
  const allowed = ['title1', 'title2'];
  const pros = handleProsNCons(prosAndCons?.pros, allowed);
  const cons = handleProsNCons(prosAndCons?.cons, allowed);
  const characteristics = getCharacteristics({
    payment_method,
    annual_account,
    meter_readings,
    edit_amount,
    usage,
    postcoderoosregeling,
  });

  return [
    {
      content: expert_quote,
      isVisible: true,
      toggleable: false,
    },
    {
      content: (
        <Div display="flex" flexDirection="column">
          {pros && <ProsCons data={pros} onlyPros />}
          {cons && <ProsCons data={cons} onlyCons />}
        </Div>
      ),
      isVisible: true,
      toggleable: false,
    },
    ...characteristics.map((object) => {
      return {
        content: object.value,
        isVisible: true,
        toggleable: false,
      };
    }),
  ];
};

export const getProductComparisonDetails = (data) => {
  const _data = { ...data, type: 'energy' };

  const { features, products, energy_label } = getProductProps(_data, 'year');

  const powerSources = energy_label ? (
    <ListComp
      groups={[
        {
          list: getEnergyLabelItemsList(energy_label),
          title: '',
        },
      ]}
      innerListVariant="comparisonProductListItem"
    />
  ) : null;

  return [
    ...getDetailsMap(features, products).map((item) => {
      return {
        content: item.value,
        isVisible: true,
        toggleable: false,
      };
    }),
    {
      content: powerSources,
      isVisible: true,
      toggleable: false,
    },
  ];
};

export const getProductComparisonRates = (data) => {
  const _data = { ...data, type: 'energy' };
  const price: Price = _data['price'] || {};
  const { month, year } = price;
  const { discountIncluded } = year;

  const rates: Rates = getAllRates(price);

  let prices: any[] = [];

  prices.push(
    discountIncluded ? formatPrice(discountIncluded + year.total) : undefined,
  );
  prices.push(discountIncluded ? formatPrice(-discountIncluded) : undefined);

  let groups: any[] = [];

  groups.push(...rates[PRODUCT_TYPE.ELECTRICITY]);

  groups.push(...rates[PRODUCT_TYPE.GAS]);

  const finalPrices = [
    ...prices,
    formatPrice(year.total),
    formatPrice(month.total),
  ];

  return [
    ...groups.map((group) => {
      return {
        content: group.value,
        isVisible: true,
        toggleable: false,
      };
    }),
    ...finalPrices.map((price) => {
      return {
        content: price,
        isVisible: true,
        toggleable: false,
      };
    }),
  ];
};

export const getProductComparisonCostBreakdown = (data) => {
  const _data = { ...data, type: 'energy' };

  const { features } = getProductProps(_data, 'year');

  console.log(features);

  return [
    {
      label: '123',
      children: ['321', '321', '432', '211'],
    },
    {
      content: <Div>Something</Div>,
      isVisible: true,
      toggleable: false,
    },
    {
      label: '123',
      children: ['321', '321', '432', '211'],
    },
  ];
};

export const energyHelpers = {
  overview: getProductComparisonOverview,
  supplier: getProductComparisonSupplier,
  details: getProductComparisonDetails,
  rates: getProductComparisonRates,
  costBreakdown: getProductComparisonCostBreakdown,
};
